import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';

// ========================================
// Types for Finance Service
// ========================================

export interface FinanceConcept {
  term: string;
  definition: string;
  example: string;
  category: string; // e.g., "stocks", "bonds", "risk", "valuation", etc.
}

export interface FinanceLesson {
  lesson_id?: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  title: string;
  text: string;
  concepts: FinanceConcept[];
  level: string; // Basic, Intermediate, Advanced
  created_at?: string;
  updated_at?: string;
}

export interface LessonSummary {
  lesson_id: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  created_at: string;
  updated_at: string;
}

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// Finance Service Class
// ========================================

export class FinanceService {
  /**
   * Build system prompt for Finance lesson generation
   */
  private static buildSystemPrompt(pastLessons?: LessonSummary[], customInstruction?: string): string {
    let prompt = `You are a finance educator creating investment lessons for learners at various levels (Basic to Advanced).

Your task is to generate an educational finance lesson focused on investor education.

REQUIREMENTS:
- Create a comprehensive lesson of 600-800 words
- Focus on practical investment knowledge and concepts
- Topics: stocks, bonds, portfolio management, valuation, risk management, market analysis, investment strategies, financial statements, market indicators, etc.
- Include clear explanations suitable for the target difficulty level
- Make the content engaging, practical, and actionable
- Indicate the difficulty level (Basic, Intermediate, or Advanced)`;

    if (pastLessons && pastLessons.length > 0) {
      const historyContext = pastLessons
        .map(l => `- "${l.lesson_name}": ${l.lesson_objective}`)
        .join('\n');
      
      prompt += `\n\nPREVIOUS LESSONS COVERED:\n${historyContext}\n\n`;
      prompt += `Generate a NEW lesson that builds upon or complements these topics. `;
      prompt += `Avoid repeating the same content from previous lessons.\n`;
    }

    prompt += `\n\nRESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "title": "Lesson title",
  "objective": "A detailed 1-2 sentence description of what this lesson covers, including the main topic and key concepts (e.g., 'Learn about portfolio diversification strategies, including asset allocation, correlation, and risk management techniques to build a balanced investment portfolio')",
  "level": "Basic" or "Intermediate" or "Advanced",
  "text": "Complete lesson text content (600-800 words)",
  "concepts": [
    {
      "term": "Financial concept or term",
      "definition": "Clear, concise definition",
      "example": "Practical example or application",
      "category": "stocks" or "bonds" or "risk" or "valuation" or "portfolio" or "market" or "strategy"
    }
  ]
}

IMPORTANT GUIDELINES:
- The objective field should be detailed and informative, describing both the topic and key concepts covered
- Include 5-8 key financial concepts that are essential to understanding the lesson
- Keep definitions clear and accessible
- Provide practical, real-world examples
- Make sure the JSON is valid and properly formatted
- Ensure the response is complete and not truncated
- Level progression:
  * Basic: Fundamental concepts, simple terminology, basic principles
  * Intermediate: More complex strategies, market mechanics, analytical methods
  * Advanced: Sophisticated analysis, advanced strategies, complex instruments`;

    if (customInstruction) {
      prompt += `\n\nCUSTOM FOCUS: ${customInstruction}`;
    }

    return prompt;
  }

  /**
   * Generate a new Finance lesson using AI and save to database
   * @param selectedLessonIds Array of lesson IDs to include as context
   * @param customInstruction Optional custom topic instruction
   */
  public static async generateLesson(
    selectedLessonIds: string[] = [],
    customInstruction?: string
  ): Promise<FinanceLesson> {
    try {
      console.log('💰 [FinanceService] Generating new finance lesson...');

      // Fetch selected past lessons for context
      let pastLessons: LessonSummary[] = [];
      if (selectedLessonIds.length > 0) {
        console.log('💰 [FinanceService] Fetching past lessons for context...');
        const allLessons = await this.getPastLessons(100);
        pastLessons = allLessons.lessons.filter(l => selectedLessonIds.includes(l.lesson_id));
      }

      const systemPrompt = this.buildSystemPrompt(pastLessons, customInstruction);
      
      let userMessage = 'Generate a new finance lesson for investors following the requirements.';
      if (customInstruction) {
        userMessage = `Generate a new finance lesson for investors following the requirements. Focus the lesson on: ${customInstruction}`;
      }

      const messages = [
        {
          role: 'user' as const,
          content: userMessage
        }
      ];

      // Call the Express.js AI provider endpoint with Claude
      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 4000,
        temperature: 0.8
      };

      console.log('💰 [FinanceService] Calling AI provider...');

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate finance lesson');
      }

      // Check if response was truncated due to max_tokens
      if (result.stop_reason === 'max_tokens') {
        console.warn('⚠️ [FinanceService] AI response was truncated due to max_tokens limit');
        throw new Error('AI response was incomplete. The lesson was too complex. Please try again.');
      }

      // Extract the AI response - handle both string and array formats
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('💰 [FinanceService] Failed to extract response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response');
      }
      
      console.log('💰 [FinanceService] Raw AI response length:', aiResponse.length);

      // Parse JSON from the response
      let lessonData: any;
      
      try {
        // Try to extract JSON from markdown code blocks if present
        const jsonMatch = aiResponse.match(/```json\n?([\s\S]*?)\n?```/) || 
                         aiResponse.match(/```\n?([\s\S]*?)\n?```/) ||
                         [null, aiResponse];
        
        const jsonString = (jsonMatch[1] || aiResponse).trim();
        
        // Check if JSON appears to be truncated (incomplete)
        if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
          console.error('💰 [FinanceService] JSON appears to be truncated');
          throw new Error('AI response was incomplete (truncated JSON)');
        }
        
        lessonData = JSON.parse(jsonString);
        
        // Validate the structure
        if (!lessonData.title || !lessonData.objective || !lessonData.text || !Array.isArray(lessonData.concepts)) {
          throw new Error('Invalid lesson structure');
        }

        console.log('💰 [FinanceService] Lesson parsed successfully:', {
          title: lessonData.title,
          objective: lessonData.objective,
          level: lessonData.level,
          textLength: lessonData.text.length,
          conceptsCount: lessonData.concepts.length
        });

      } catch (parseError) {
        console.error('💰 [FinanceService] Failed to parse AI response:', parseError);
        
        // If it's a truncation error, throw it directly for better user feedback
        if (parseError instanceof Error && 
            (parseError.message.includes('truncated') || 
             parseError.message.includes('Unexpected end of JSON') ||
             parseError.message.includes('Unexpected token'))) {
          throw new Error('AI response was incomplete. The lesson was too complex. Please try again.');
        }
        
        throw parseError;
      }

      // Save lesson to database
      const savedLesson = await this.saveLesson(lessonData);
      return savedLesson;

    } catch (error) {
      console.error('💰 [FinanceService] Error generating lesson:', error);
      
      // Handle authentication errors
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate finance lessons.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Save lesson to database
   */
  public static async saveLesson(lessonData: {
    title: string;
    objective: string;
    level: string;
    text: string;
    concepts: FinanceConcept[];
  }): Promise<FinanceLesson> {
    try {
      const result = await apiCall('/lessons', {
        method: 'POST',
        body: JSON.stringify({
          category: 'finance-investor',
          lesson_name: lessonData.title,
          lesson_objective: lessonData.objective,
          lesson_content: {
            text: lessonData.text,
            concepts: lessonData.concepts,
            level: lessonData.level
          }
        })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to save lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const savedLesson: FinanceLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        title: data.lesson_name,
        text: data.lesson_content.text,
        concepts: data.lesson_content.concepts || [],
        level: data.lesson_content.level || 'Basic',
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return savedLesson;
    } catch (error) {
      console.error('💰 [FinanceService] Error saving lesson:', error);
      throw error;
    }
  }

  /**
   * Get past lessons for user (category: finance-investor)
   */
  public static async getPastLessons(limit: number = 50, offset: number = 0): Promise<{
    lessons: LessonSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/lessons?category=finance-investor&limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lessons');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      return {
        lessons: data.lessons || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('💰 [FinanceService] Error fetching past lessons:', error);
      throw error;
    }
  }

  /**
   * Get specific lesson by ID
   */
  public static async getLessonById(lessonId: string): Promise<FinanceLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: FinanceLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        title: data.lesson_name,
        text: data.lesson_content.text,
        concepts: data.lesson_content.concepts || [],
        level: data.lesson_content.level || 'Basic',
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('💰 [FinanceService] Error fetching lesson:', error);
      throw error;
    }
  }

  /**
   * Update lesson name
   */
  public static async updateLessonName(lessonId: string, newName: string): Promise<FinanceLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({ lesson_name: newName })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: FinanceLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        title: data.lesson_name,
        text: data.lesson_content.text,
        concepts: data.lesson_content.concepts || [],
        level: data.lesson_content.level || 'Basic',
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('💰 [FinanceService] Error updating lesson:', error);
      throw error;
    }
  }

  /**
   * Delete lesson
   */
  public static async deleteLesson(lessonId: string): Promise<void> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete lesson');
      }
    } catch (error) {
      console.error('💰 [FinanceService] Error deleting lesson:', error);
      throw error;
    }
  }
}

